import { isSafeUrl } from "components/helpers";

// Draft.js was archived in February 2023, so its serialisation format is
// frozen and this converter is write-once. It runs on the render path of
// every note saved before the Lexical migration, and there is no backfill,
// so it must be total: an unknown block becomes a paragraph and an unknown
// entity becomes plain text rather than raising.

const DRAFT_STYLE_BITS = {
  BOLD: 1,
  ITALIC: 2,
  STRIKETHROUGH: 4,
  UNDERLINE: 8,
  CODE: 16,
};

const HEADING_TAGS = {
  "header-one": "h1",
  "header-two": "h2",
  "header-three": "h3",
  "header-four": "h4",
  "header-five": "h5",
  "header-six": "h6",
};

const LIST_TYPES = {
  "unordered-list-item": "bullet",
  "ordered-list-item": "number",
};

const ELEMENT_FORMATS = new Set(["left", "center", "right", "justify"]);

// IS_DIRECTIONLESS, matching what $createMentionNode applies in the editor.
const MENTION_DETAIL = 1;

export function isDraftRawState(value) {
  return Array.isArray(value?.blocks);
}

export function draftRawToLexicalJson(raw) {
  const blocks = Array.isArray(raw?.blocks) ? raw.blocks : [];
  const entityMap = isObject(raw?.entityMap) ? raw.entityMap : {};
  const children = convertBlocks(blocks, entityMap);
  if (children.length === 0) {
    children.push(elementNode("paragraph", [], "", {}));
  }
  return {
    root: {
      type: "root",
      children,
      direction: null,
      format: "",
      indent: 0,
      version: 1,
    },
  };
}

function isObject(value) {
  return typeof value === "object" && value !== null;
}

function elementNode(type, children, format, extra) {
  return {
    type,
    children,
    direction: null,
    format,
    indent: 0,
    version: 1,
    ...extra,
  };
}

function textNode(text, format) {
  return {
    type: "text",
    text,
    format,
    detail: 0,
    mode: "normal",
    style: "",
    version: 1,
  };
}

function mentionNode(text, format, mentionName, userId) {
  return {
    type: "mention",
    text,
    format,
    detail: MENTION_DETAIL,
    mode: "segmented",
    style: "",
    version: 1,
    mentionName,
    userId,
  };
}

function listNode(listType) {
  return elementNode("list", [], "", {
    listType,
    start: 1,
    tag: listType === "number" ? "ol" : "ul",
  });
}

function listItemNode(value, children, format) {
  return elementNode("listitem", children, format, { value });
}

function alignFormat(block) {
  const alignment = isObject(block.data) ? block.data["text-align"] : undefined;
  return ELEMENT_FORMATS.has(alignment) ? alignment : "";
}

function convertBlocks(blocks, entityMap) {
  const children = [];
  // Open list nodes, innermost last. Draft records nesting as a flat depth
  // per block; Lexical needs real nesting, and node transforms do not run
  // during parseEditorState, so the nesting has to be built here.
  let openLists = [];

  for (const block of blocks) {
    if (!isObject(block)) {
      continue;
    }
    const listType = LIST_TYPES[block.type];
    if (!listType) {
      openLists = [];
      children.push(convertBlock(block, entityMap));
      continue;
    }

    const depth = Math.max(0, Number(block.depth) || 0);
    while (openLists.length > 0 && last(openLists).depth > depth) {
      openLists.pop();
    }
    if (
      openLists.length > 0 &&
      last(openLists).depth === depth &&
      last(openLists).listType !== listType
    ) {
      openLists.pop();
    }
    if (openLists.length === 0 || last(openLists).depth < depth) {
      openLists.push(openList(children, openLists, listType, depth));
    }

    const list = last(openLists).list;
    list.children.push(
      listItemNode(
        list.children.length + 1,
        convertInline(block, entityMap),
        alignFormat(block)
      )
    );
  }

  return children;
}

function openList(rootChildren, openLists, listType, depth) {
  const list = listNode(listType);
  if (openLists.length === 0) {
    rootChildren.push(list);
  } else {
    // Lexical hangs a nested list off a list item of its parent; the theme
    // hides that item's own marker.
    const parent = last(openLists).list;
    parent.children.push(listItemNode(parent.children.length + 1, [list], ""));
  }
  return { list, listType, depth };
}

function last(items) {
  return items[items.length - 1];
}

function convertBlock(block, entityMap) {
  const children = convertInline(block, entityMap);
  const format = alignFormat(block);
  const headingTag = HEADING_TAGS[block.type];
  if (headingTag) {
    return elementNode("heading", children, format, { tag: headingTag });
  }
  if (block.type === "blockquote") {
    return elementNode("quote", children, format, {});
  }
  if (block.type === "code-block") {
    return elementNode("code", children, format, { language: null });
  }
  return elementNode("paragraph", children, format, {});
}

function convertInline(block, entityMap) {
  const text = typeof block.text === "string" ? block.text : "";
  if (text.length === 0) {
    return [];
  }
  const styleRanges = asRanges(block.inlineStyleRanges, text.length);
  const entityRanges = asRanges(block.entityRanges, text.length);
  const slices = sliceText(text, styleRanges, entityRanges);

  const children = [];
  for (const group of groupByEntity(slices)) {
    const entity = group.entityKey === null ? null : entityMap[group.entityKey];
    children.push(...convertEntityGroup(group, entity, text));
  }
  return children;
}

function asRanges(ranges, textLength) {
  if (!Array.isArray(ranges)) {
    return [];
  }
  return ranges
    .filter(isObject)
    .map(range => ({
      ...range,
      start: clamp(Number(range.offset) || 0, textLength),
      end: clamp(
        (Number(range.offset) || 0) + (Number(range.length) || 0),
        textLength
      ),
    }))
    .filter(range => range.end > range.start);
}

function clamp(value, max) {
  return Math.min(Math.max(value, 0), max);
}

function sliceText(text, styleRanges, entityRanges) {
  const boundaries = new Set([0, text.length]);
  for (const range of [...styleRanges, ...entityRanges]) {
    boundaries.add(snapToCharacter(text, range.start));
    boundaries.add(snapToCharacter(text, range.end));
  }
  const points = [...boundaries].sort((a, b) => a - b);

  const slices = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];
    const entity = entityRanges.find(
      range => range.start <= start && range.end >= end
    );
    slices.push({
      start,
      end,
      text: text.slice(start, end),
      format: styleRanges.reduce(
        (format, range) =>
          range.start <= start && range.end >= end
            ? format | (DRAFT_STYLE_BITS[range.style] || 0)
            : format,
        0
      ),
      entityKey: entity ? entity.key : null,
    });
  }
  return slices;
}

// Draft offsets are UTF-16 code units and can land between the halves of a
// surrogate pair. Splitting there would emit lone surrogates, so the
// boundary moves to the start of the character instead.
function snapToCharacter(text, index) {
  const code = text.charCodeAt(index);
  const previous = text.charCodeAt(index - 1);
  const isLowSurrogate = code >= 0xdc00 && code <= 0xdfff;
  const followsHighSurrogate = previous >= 0xd800 && previous <= 0xdbff;
  return isLowSurrogate && followsHighSurrogate ? index - 1 : index;
}

function groupByEntity(slices) {
  const groups = [];
  for (const slice of slices) {
    const current = last(groups);
    if (
      current &&
      slice.entityKey !== null &&
      current.entityKey === slice.entityKey
    ) {
      current.slices.push(slice);
    } else {
      groups.push({ entityKey: slice.entityKey, slices: [slice] });
    }
  }
  return groups;
}

function convertEntityGroup(group, entity, text) {
  const type = String(entity?.type ?? "");
  const data = isObject(entity?.data) ? entity.data : {};
  const plain = group.slices.map(slice => textNode(slice.text, slice.format));

  if (type.toUpperCase() === "LINK") {
    const url = data.url ?? data.href;
    // An unsafe protocol degrades to plain text rather than becoming an href.
    if (typeof url !== "string" || !isSafeUrl(url)) {
      return plain;
    }
    return [
      elementNode("link", plain, "", {
        url,
        rel: "noopener noreferrer",
        target: "_blank",
        title: null,
      }),
    ];
  }

  if (type.toLowerCase() === "mention") {
    const mention = isObject(data.mention) ? data.mention : {};
    const covered = text.slice(group.slices[0].start, last(group.slices).end);
    return [
      mentionNode(
        covered,
        group.slices[0].format,
        mention.name ?? covered,
        mention.id ?? null
      ),
    ];
  }

  return plain;
}
